import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import {
  CommissionPlanResponse,
  CreateCommissionPlanRequest,
  UpdateCommissionPlanRequest,
  ListCommissionPlansRequest,
  CreateResponseData,
  UpdateResponseData,
  OwnershipLevel,
  StaffAssignmentResponse,
  AssignStaffRequest,
  StaffPlanAssignmentsResponse
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class CommissionPlanService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL = environment.APP_URL;
  
  // State management for list view
  private selectedOwnershipLevel: OwnershipLevel = OwnershipLevel.Company;
  private selectedFilter: {name: string, value: string} | undefined;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  // State management methods
  setSelectedOwnershipLevel(level: OwnershipLevel): void {
    this.selectedOwnershipLevel = level;
  }

  getSelectedOwnershipLevel(): OwnershipLevel {
    return this.selectedOwnershipLevel;
  }

  setSelectedFilter(filter: {name: string, value: string} | undefined): void {
    this.selectedFilter = filter;
  }

  getSelectedFilter(): {name: string, value: string} | undefined {
    return this.selectedFilter;
  }

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Get commission plans by ownership level
  getCommissionPlansByOwnership(
    ownership: OwnershipLevel,
    tenantId: number,
    franchiseGroupId: number,
    request?: ListCommissionPlansRequest
  ): Observable<CommissionPlanResponse[]> {
    let url = '';
    switch (ownership) {
      case OwnershipLevel.Company:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_COMMISSION_PLANS;
        break;
      case OwnershipLevel.FranchiseGroup:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_FRANCHISE_GROUP_COMMISSION_PLANS
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_TENANT_COMMISSION_PLANS
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_COMMISSION_PLANS;
        break;
    }

    const params: Record<string, unknown> = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10
    };
    
    if (request?.search_term) {
      params['SearchTerm'] = request.search_term;
    }
    if (request?.status !== undefined) {
      params['Status'] = request.status;
    }
    
    return this.http.get<CommissionPlanResponse[]>(url, {
      headers: this.httpOptions.headers,
      params: this.buildParams(params)
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Get single commission plan by ownership level
  getCommissionPlanByOwnership(
    ownership: OwnershipLevel,
    planId: number,
    tenantId: number,
    franchiseGroupId: number
  ): Observable<CommissionPlanResponse> {
    let url = '';
    switch (ownership) {
      case OwnershipLevel.Company:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_COMMISSION_PLAN_BY_ID
          .replace('{plan_id}', planId.toString());
        break;
      case OwnershipLevel.FranchiseGroup:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_FRANCHISE_GROUP_COMMISSION_PLAN_BY_ID
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{plan_id}', planId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_TENANT_COMMISSION_PLAN_BY_ID
          .replace('{tenant_id}', tenantId.toString())
          .replace('{plan_id}', planId.toString());
        break;
      default:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_COMMISSION_PLAN_BY_ID
          .replace('{plan_id}', planId.toString());
        break;
    }

    return this.http.get<CommissionPlanResponse>(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Create commission plan by ownership level
  createCommissionPlanByOwnership(
    ownership: OwnershipLevel,
    request: CreateCommissionPlanRequest,
    tenantId: number,
    franchiseGroupId: number
  ): Observable<CreateResponseData> {
    let url = '';
    switch (ownership) {
      case OwnershipLevel.Company:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_COMPANY_COMMISSION_PLAN;
        break;
      case OwnershipLevel.FranchiseGroup:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_FRANCHISE_GROUP_COMMISSION_PLAN
          .replace('{franchise_group_id}', franchiseGroupId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_TENANT_COMMISSION_PLAN
          .replace('{tenant_id}', tenantId.toString());
        break;
      default:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_COMPANY_COMMISSION_PLAN;
        break;
    }

    return this.http.post<CreateResponseData>(url, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Update commission plan by ownership level
  updateCommissionPlanByOwnership(
    ownership: OwnershipLevel,
    planId: number,
    request: UpdateCommissionPlanRequest,
    tenantId: number,
    franchiseGroupId: number
  ): Observable<UpdateResponseData> {
    let url = '';
    switch (ownership) {
      case OwnershipLevel.Company:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_COMPANY_COMMISSION_PLAN
          .replace('{plan_id}', planId.toString());
        break;
      case OwnershipLevel.FranchiseGroup:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_FRANCHISE_GROUP_COMMISSION_PLAN
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{plan_id}', planId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_TENANT_COMMISSION_PLAN
          .replace('{tenant_id}', tenantId.toString())
          .replace('{plan_id}', planId.toString());
        break;
      default:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_COMPANY_COMMISSION_PLAN
          .replace('{plan_id}', planId.toString());
        break;
    }

    return this.http.put<UpdateResponseData>(url, request, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Deactivate commission plan by ownership level
  deactivateCommissionPlanByOwnership(
    ownership: OwnershipLevel,
    planId: number,
    tenantId: number,
    franchiseGroupId: number
  ): Observable<void> {
    let url = '';
    switch (ownership) {
      case OwnershipLevel.Company:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_COMPANY_COMMISSION_PLAN
          .replace('{plan_id}', planId.toString());
        break;
      case OwnershipLevel.FranchiseGroup:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_FRANCHISE_GROUP_COMMISSION_PLAN
          .replace('{franchise_group_id}', franchiseGroupId.toString())
          .replace('{plan_id}', planId.toString());
        break;
      case OwnershipLevel.Tenant:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_TENANT_COMMISSION_PLAN
          .replace('{tenant_id}', tenantId.toString())
          .replace('{plan_id}', planId.toString());
        break;
      default:
        url = this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_COMPANY_COMMISSION_PLAN
          .replace('{plan_id}', planId.toString());
        break;
    }

    return this.http.delete<void>(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Legacy methods for backward compatibility (Company-level only)
  getCompanyCommissionPlans(request?: ListCommissionPlansRequest): Observable<CommissionPlanResponse[]> {
    return this.getCommissionPlansByOwnership(OwnershipLevel.Company, 0, 0, request);
  }

  getCompanyCommissionPlan(planId: number): Observable<CommissionPlanResponse> {
    return this.getCommissionPlanByOwnership(OwnershipLevel.Company, planId, 0, 0);
  }

  createCompanyCommissionPlan(request: CreateCommissionPlanRequest): Observable<CreateResponseData> {
    return this.createCommissionPlanByOwnership(OwnershipLevel.Company, request, 0, 0);
  }

  updateCompanyCommissionPlan(planId: number, request: UpdateCommissionPlanRequest): Observable<UpdateResponseData> {
    return this.updateCommissionPlanByOwnership(OwnershipLevel.Company, planId, request, 0, 0);
  }

  deactivateCompanyCommissionPlan(planId: number): Observable<void> {
    return this.deactivateCommissionPlanByOwnership(OwnershipLevel.Company, planId, 0, 0);
  }

  // Tenant-level commission plan operations
  getTenantCommissionPlans(tenantId: number, request?: ListCommissionPlansRequest): Observable<CommissionPlanResponse[]> {
    const params: Record<string, unknown> = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10,
    };
    
    if (request?.search_term) {
      params['SearchTerm'] = request.search_term;
    }
    if (request?.status !== undefined) {
      params['Status'] = request.status;
    }
    
    return this.http.get<CommissionPlanResponse[]>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_TENANT_COMMISSION_PLANS
        .replace('{tenant_id}', tenantId.toString()),
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTenantCommissionPlan(tenantId: number, planId: number): Observable<CommissionPlanResponse> {
    return this.http.get<CommissionPlanResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_TENANT_COMMISSION_PLAN_BY_ID
        .replace('{tenant_id}', tenantId.toString())
        .replace('{plan_id}', planId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createTenantCommissionPlan(tenantId: number, request: CreateCommissionPlanRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.CREATE_TENANT_COMMISSION_PLAN
        .replace('{tenant_id}', tenantId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateTenantCommissionPlan(tenantId: number, planId: number, request: UpdateCommissionPlanRequest): Observable<UpdateResponseData> {
    return this.http.put<UpdateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.UPDATE_TENANT_COMMISSION_PLAN
        .replace('{tenant_id}', tenantId.toString())
        .replace('{plan_id}', planId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  deactivateTenantCommissionPlan(tenantId: number, planId: number): Observable<void> {
    return this.http.delete<void>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.DELETE_TENANT_COMMISSION_PLAN
        .replace('{tenant_id}', tenantId.toString())
        .replace('{plan_id}', planId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Staff Assignment Methods
  getStaffAssignments(planId: number): Observable<StaffAssignmentResponse> {
    return this.http.get<StaffAssignmentResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.GET_COMMISSION_PLAN_STAFF_ASSIGNMENTS
        .replace('{plan_id}', planId.toString()),
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  assignStaff(planId: number, request: AssignStaffRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.CREATE_COMMISSION_PLAN_STAFF_ASSIGNMENT
        .replace('{plan_id}', planId.toString()),
      request,
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  removeStaffAssignment(planId: number, assignmentId: number): Observable<void> {
    return this.http.delete<void>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.DELETE_COMMISSION_PLAN_STAFF_ASSIGNMENT
        .replace('{plan_id}', planId.toString())
        .replace('{assignment_id}', assignmentId.toString()),
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  // Staff Plan Assignments Methods (for list page)
  getStaffPlanAssignments(tenantId: number, pageNumber: number = 1, pageSize: number = 10): Observable<StaffPlanAssignmentsResponse> {
    const params: Record<string, unknown> = {
      PageNumber: pageNumber,
      PageSize: pageSize
    };

    return this.http.get<StaffPlanAssignmentsResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMMISSION.GET_COMPANY_TENANT_STAFF_PLAN_ASSIGNMENTS
        .replace('{tenant_id}', tenantId.toString()),
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }

  assignPlanToStaff(planId: number, request: AssignStaffRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.COMPANIES.CREATE_COMMISSION_PLAN_STAFF_ASSIGNMENT
        .replace('{plan_id}', planId.toString()),
      request,
      this.httpOptions
    ).pipe(
      map((res) => res),
      catchError((error) => {
        return throwError(() => new AppErrors(error));
      })
    );
  }
}

